import * as React from 'react'
import axios from 'axios'

// Prices the selected plan through POST /billing/quote whenever the plan,
// country, VAT number or coupon changes. One Chargebee estimate, no customer
// and no payment intent, so it is safe to run on every change.
//
// Answers are tagged with a sequence number so a slow reply can never
// overwrite a newer one. A failed quote clears the amount: its inputs really
// changed (a coupon only becomes an input once useCouponCheck accepted it), so
// the previous amount would price something else.
//
// `prime(inputs, quote)` hands over an answer already fetched for exactly
// those inputs (the coupon check), so adopting them costs no second estimate.
export function quoteRequestBody({ planId, country, vatNumber, coupon }) {
    return {
        plan: planId,
        country: country || undefined,
        vatNumber: vatNumber || undefined,
        coupon: coupon || undefined,
    }
}

const requestKey = body => JSON.stringify(body)

export function useCheckoutQuote({
    planId,
    country,
    vatNumber,
    coupon,
    enabled = true,
    debounceMs = 300,
}) {
    const [state, setState] = React.useState({
        status: 'idle',
        quote: null,
        error: null,
    })
    const sequenceRef = React.useRef(0)
    const primedRef = React.useRef(null)

    const prime = React.useCallback((inputs, quote) => {
        primedRef.current = {
            key: requestKey(quoteRequestBody(inputs)),
            quote,
        }
    }, [])

    React.useEffect(() => {
        if (!enabled || !planId) return undefined
        const sequence = ++sequenceRef.current
        const body = quoteRequestBody({ planId, country, vatNumber, coupon })
        const primed = primedRef.current
        primedRef.current = null
        if (primed && primed.key === requestKey(body)) {
            setState({ status: 'ready', quote: primed.quote, error: null })
            return undefined
        }
        setState(current => ({ ...current, status: 'loading', error: null }))
        const timer = setTimeout(() => {
            axios
                .post('/billing/quote', body)
                .then(response => {
                    if (sequence !== sequenceRef.current) return
                    setState({
                        status: 'ready',
                        quote: response.data,
                        error: null,
                    })
                })
                .catch(error => {
                    if (sequence !== sequenceRef.current) return
                    setState({ status: 'failed', quote: null, error })
                })
        }, debounceMs)
        return () => clearTimeout(timer)
    }, [enabled, planId, country, vatNumber, coupon, debounceMs])

    return { ...state, prime }
}
